import React, { useState, useRef } from 'react';
import { PlatformConfig } from '../../core/platform/types';
import { PhotoGalleryManager } from './PhotoGalleryManager';
import { VariablesEditor } from './VariablesEditor';
import { PromptEditor, PromptEditorHandle } from './PromptEditor';
import { PCHELP_SUPPORT_PROMPT } from '../../core/platform/promptTemplates';
import { 
  IconCheck, 
  IconClose, 
  IconImage, 
  IconLink, 
  IconCpu, 
  IconAlert,
  IconPlus,
  IconTrash
} from './Icons';

interface CreateInstanceFormProps {
  onSubmit: (data: any) => Promise<void> | void;
  onCancel?: () => void;
}

export const CreateInstanceForm: React.FC<CreateInstanceFormProps> = ({ 
  onSubmit, 
  onCancel 
}) => {
  const [activeTab, setActiveTab] = useState<'ai' | 'profile' | 'links'>('ai');
  const [isSaving, setIsSaving] = useState(false);
  const [saveStep, setSaveStep] = useState<number>(1);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const [formData, setFormData] = useState({
    id: `bot_${Date.now().toString().slice(-4)}`,
    platform: 'telegram',
    name: 'Алексей',
    agentName: 'Алексей',
    age: 28,
    country: 'Россия, Москва',
    avatarUrl: '',
    photos: [] as string[],
    webLink: 'https://pchelp.linkpc.net',
    webMedia: '',
    tgProfile: '@pchelp_support',
    tlgLink: '',
    behaviorPreset: 'support' as PlatformConfig['behaviorPreset'],
    systemPrompt: PCHELP_SUPPORT_PROMPT,
    aiModel: 'gemini-3.5-flash',
    aiApiKey: '',
    minDelay: 2,
    maxDelay: 15,
    allowSilence: true,
    variables: {
      service_history: 'Первичное обращение (история отсутствует)'
    } as Record<string, string>
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSaving) return;

    setIsSaving(true);
    setSaveError(null);
    setSaveStep(1);

    const stepTimer = setTimeout(() => {
      setSaveStep(2);
    }, 600);

    try {
      await onSubmit(formData);
      clearTimeout(stepTimer);
      setSaveStep(3);
      setIsSuccess(true);
      setTimeout(() => {
        if (onCancel) onCancel();
      }, 700);
    } catch (err: any) {
      clearTimeout(stepTimer);
      setIsSaving(false);
      setSaveError(err?.message || 'Ошибка при создании нового бота');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : (type === 'number' ? Number(value) : value)
    }));
  };

  const promptEditorRef = useRef<PromptEditorHandle>(null);

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget && !isSaving && onCancel) onCancel(); }}>
      <div className="instance-modal">
        {/* Индикатор прогресса вверху модалки */}
        {isSaving && (
          <div className="modal-progress-bar">
            <div className="modal-progress-bar-inner"></div>
          </div>
        )}

        {/* Оверлей блокировки и статуса при сохранении */}
        {isSaving && (
          <div className="modal-saving-overlay">
            <div className="modal-saving-card">
              {isSuccess ? (
                <div className="saving-success-icon">
                  <IconCheck size={24} />
                </div>
              ) : (
                <div className="saving-spinner-ring"></div>
              )}

              <div className="modal-saving-title">
                {isSuccess ? 'БОТ УСПЕШНО СОЗДАН' : 'РЕГИСТРАЦИЯ ИНСТАНСА'}
              </div>

              <div className="modal-saving-desc">
                {isSuccess 
                  ? 'Конфигурация бота сохранена и подготовлена к запуску.'
                  : 'Запись параметров конфигурации бота в распределённую базу данных...'}
              </div>

              <div className="modal-saving-steps">
                <div className={`modal-saving-step ${saveStep >= 1 ? (saveStep > 1 ? 'done' : 'active') : ''}`}>
                  <span className="step-icon">{saveStep > 1 ? <IconCheck size={12} /> : '•'}</span>
                  <span>Валидация параметров и медиа-активов</span>
                </div>
                <div className={`modal-saving-step ${saveStep >= 2 ? (saveStep > 2 ? 'done' : 'active') : ''}`}>
                  <span className="step-icon">{saveStep > 2 ? <IconCheck size={12} /> : '•'}</span>
                  <span>Создание инстанса и сохранение профиля в БД</span>
                </div>
                <div className={`modal-saving-step ${saveStep >= 3 ? 'done' : ''}`}>
                  <span className="step-icon">{saveStep >= 3 ? <IconCheck size={12} /> : '○'}</span>
                  <span>Инициализация завершена</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="modal-header">
          <div className="modal-title-wrap">
            <div className="modal-avatar-preview">
              {formData.avatarUrl ? (
                <img src={formData.avatarUrl} alt={formData.name} onError={(e)=>{ (e.target as HTMLElement).style.display = 'none'; }} />
              ) : (
                <span>{formData.name ? formData.name[0].toUpperCase() : 'B'}</span>
              )}
            </div>
            <div>
              <div className="modal-title-row">
                <h3>НОВЫЙ ИНСТАНС БОТА</h3>
                <span className="modal-id-badge">ID: {formData.id}</span>
              </div>
              <p className="modal-subtitle">Инициализация параметров профиля, медиа-галереи и стиля ведения диалога</p>
            </div>
          </div>
          {onCancel && (
            <button 
              type="button" 
              className="close-btn" 
              disabled={isSaving}
              onClick={() => !isSaving && onCancel()}
              title="Закрыть окно"
            >
              <IconClose size={16} />
            </button>
          )}
        </div>

        {/* Сообщение об ошибке, если создание не удалось */}
        {saveError && (
          <div className="alert-box error" style={{ margin: '1rem 1.5rem 0' }}>
            <IconAlert size={14} />
            <span>{saveError}</span>
            <button type="button" onClick={() => setSaveError(null)} className="alert-close-btn">&times;</button>
          </div>
        )}

        {/* Tabs */}
        <div className="tabs-nav">
          <button
            type="button"
            className={`tab-btn ${activeTab === 'ai' ? 'active' : ''}`}
            onClick={() => !isSaving && setActiveTab('ai')}
            disabled={isSaving}
          >
            <IconCpu size={14} />
            <span>ПОВЕДЕНИЕ И AI</span>
          </button>
          <button
            type="button"
            className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => !isSaving && setActiveTab('profile')}
            disabled={isSaving}
          >
            <IconImage size={14} />
            <span>ПРОФИЛЬ И ФОТО ({formData.photos.length})</span>
          </button>
          <button
            type="button"
            className={`tab-btn ${activeTab === 'links' ? 'active' : ''}`}
            onClick={() => !isSaving && setActiveTab('links')}
            disabled={isSaving}
          >
            <IconLink size={14} />
            <span>КОНТАКТЫ И ССЫЛКИ</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <fieldset disabled={isSaving} style={{ border: 'none', padding: 0, margin: 0 }}>
          {/* TAB 1: AI & BEHAVIOR */}
          {activeTab === 'ai' && (
            <div className="tab-pane">
              <div className="form-row-2">
                <div className="form-group">
                  <label>СТИЛЬ ОБЩЕНИЯ (ПРЕСЕТ):</label>
                  <select name="behaviorPreset" value={formData.behaviorPreset} onChange={handleChange}>
                    <option value="support">Инженер техподдержки PCHelp ({"{{agent_name}}"})</option>
                    <option value="business">Деловая (Business) — формальный тон</option>
                    <option value="friendly">Дружелюбная (Friendly) — мягкое общение</option>
                    <option value="flirty">Кокетливая (Flirty) — подогрев и вовлечение</option>
                    <option value="mysterious">Загадочная (Mysterious) — интрига и недосказанность</option>
                    <option value="custom">Пользовательский (Custom) — собственный промпт</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>НЕЙРОСЕТЕВАЯ МОДЕЛЬ (AI):</label>
                  <select name="aiModel" value={formData.aiModel} onChange={handleChange}>
                    <option value="gemini-3.5-flash">Gemini 3.5 Flash (Рекомендуется)</option>
                    <option value="gemini-3.5-flash-lite">Gemini 3.5 Flash Lite (Быстрая / низкая нагрузка)</option>
                    <option value="gemini-3.8-flash">Gemini 3.8 Flash</option>
                    <option value="gpt-4o-mini">GPT-4o Mini</option>
                    <option value="gpt-4o">GPT-4o</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>КЛЮЧ AI API (GEMINI / OPENAI API KEY):</label>
                <input
                  type="password"
                  name="aiApiKey"
                  placeholder="AIzaSy... (или оставьте пустым для системного ключа из окружения)"
                  value={formData.aiApiKey}
                  onChange={handleChange}
                  autoComplete="off"
                />
                <span className="field-hint">Ключ Google AI Studio (Gemini) для генерации ответов нейросетью</span>
              </div>

              <div className="form-group">
                <label>ИМЯ ИНЖЕНЕРА / СПЕЦИАЛИСТА ({"{{agent_name}}"}):</label>
                <input
                  type="text"
                  name="agentName"
                  placeholder="Алексей"
                  value={formData.agentName}
                  onChange={handleChange}
                />
                <span className="field-hint">Подставляется в {"{{agent_name}}"} (по умолчанию имя бота)</span>
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label>МИН. ЗАДЕРЖКА ОТВЕТА (СЕК):</label>
                  <input
                    type="number"
                    name="minDelay"
                    min={1}
                    max={60}
                    value={formData.minDelay}
                    onChange={handleChange}
                  />
                  <span className="field-hint">Имитация набора текста человеком</span>
                </div>
                <div className="form-group">
                  <label>МАКС. ЗАДЕРЖКА ОТВЕТА (СЕК):</label>
                  <input
                    type="number"
                    name="maxDelay"
                    min={2}
                    max={120}
                    value={formData.maxDelay}
                    onChange={handleChange}
                  />
                  <span className="field-hint">Верхняя граница случайной паузы</span>
                </div>
              </div>

              {/* Опция молчания бота */}
              <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                <label className="form-checkbox-card">
                  <input
                    type="checkbox"
                    name="allowSilence"
                    checked={formData.allowSilence}
                    onChange={handleChange}
                  />
                  <div>
                    <div className="form-checkbox-card-title">ПРОМОЛЧАТЬ В ОТВЕТ, ЕСЛИ СООБЩЕНИЕ НЕ ТРЕБУЕТ РЕАКЦИИ</div>
                    <div className="form-checkbox-card-desc">
                      Бот не будет спамить и завершит реплику молчанием, если собеседник написал «Спасибо», «Ок», согласился или если диалог исчерпан.
                    </div>
                  </div>
                </label>
              </div>

              {/* Редактор переменных бота */}
              <VariablesEditor
                variables={formData.variables}
                onChange={(vars) => setFormData(prev => ({ ...prev, variables: vars }))}
              />

              <div className="form-group" style={{ marginTop: '1.25rem' }}>
                <label>СИСТЕМНЫЙ ПРОМПТ С ПЕРЕМЕННЫМИ И ВЫЗОВАМИ ФУНКЦИЙ:</label>
                <PromptEditor
                  ref={promptEditorRef}
                  value={formData.systemPrompt}
                  onChange={(val) => setFormData(prev => ({ ...prev, systemPrompt: val }))}
                  variables={formData.variables}
                  agentName={formData.agentName || formData.name}
                  rows={10}
                  placeholder="Оставьте пустым для использования стандартного шаблона пресета..."
                />
                <span className="field-hint">
                  Поддерживается подсветка синтаксиса, вставка в позицию курсора, функции {'{{curl(url)}}'} и получение значений при наведении мыши (XHR).
                </span>
              </div>
            </div>
          )}

          {/* TAB 2: PROFILE & PHOTOS */}
          {activeTab === 'profile' && (
            <div className="tab-pane">
              <div className="section-label">
                <h4>ФОТОАЛЬБОМ ПЕРСОНАЖА:</h4>
                <p>Загружайте изображения через Ctrl+V, Drag & Drop или ссылки. Назначьте главное фото аватаром.</p>
              </div>

              <PhotoGalleryManager
                photos={formData.photos}
                avatarUrl={formData.avatarUrl}
                onChangePhotos={(newPhotos, newAvatar) => {
                  setFormData(prev => ({
                    ...prev,
                    photos: newPhotos,
                    avatarUrl: newAvatar
                  }));
                }}
              />

              <div className="form-row-2" style={{ marginTop: '1.25rem' }}>
                <div className="form-group">
                  <label>ИДЕНТИФИКАТОР ИНСТАНСА (ID):</label>
                  <input
                    type="text"
                    name="id"
                    value={formData.id}
                    onChange={handleChange}
                    required
                  />
                  <span className="field-hint">Уникальный ID в системе и Redis</span>
                </div>
                <div className="form-group">
                  <label>ПЛАТФОРМА:</label>
                  <select name="platform" value={formData.platform} onChange={handleChange}>
                    <option value="telegram">Telegram Web</option>
                  </select>
                </div>
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label>ИМЯ БОТА (В TELEGRAM):</label>
                  <input
                    type="text"
                    name="name"
                    placeholder="Например: Анна"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                  <span className="field-hint">Синхронизируется с профилем Telegram Web</span>
                </div>
                <div className="form-group">
                  <label>ЮЗЕРНЕЙМ БОТА (@USERNAME):</label>
                  <input
                    type="text"
                    name="tgProfile"
                    placeholder="@username"
                    value={formData.tgProfile}
                    onChange={handleChange}
                    required
                  />
                  <span className="field-hint">Синхронизируется с профилем Telegram Web</span>
                </div>
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label>ВОЗРАСТ:</label>
                  <input
                    type="number"
                    name="age"
                    min={18}
                    max={65}
                    value={formData.age}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>СТРАНА / ГОРОД:</label>
                  <input
                    type="text"
                    name="country"
                    placeholder="Например: Армения, Ереван"
                    value={formData.country}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: LINKS */}
          {activeTab === 'links' && (
            <div className="tab-pane">
              <div className="form-row-2">
                <div className="form-group">
                  <label>TELEGRAM ЮЗЕРНЕЙМ ПРОФИЛЯ:</label>
                  <input
                    type="text"
                    name="tgProfile"
                    placeholder="@username"
                    value={formData.tgProfile}
                    onChange={handleChange}
                    required
                  />
                  <span className="field-hint">Синхронизируется в Telegram Web</span>
                </div>
                <div className="form-group">
                  <label>TELEGRAM КОНТАКТНАЯ ССЫЛКА / КАНАЛ:</label>
                  <input
                    type="text"
                    name="tlgLink"
                    placeholder="@channel_or_link"
                    value={formData.tlgLink}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>ВЕБ-ССЫЛКА (ПАРТНЕРСКАЯ / РЕГИСТРАЦИЯ):</label>
                <input
                  type="url"
                  name="webLink"
                  placeholder="https://..."
                  value={formData.webLink}
                  onChange={handleChange}
                  required
                />
                <span className="field-hint">Отправляется в диалогах для конверсии пользователя</span>
              </div>

              <div className="form-group">
                <label>ССЫЛКА НА ВНЕШНИЕ МЕДИА (ОБЛАКО):</label>
                <input
                  type="url"
                  name="webMedia"
                  placeholder="https://drive.google.com/..."
                  value={formData.webMedia}
                  onChange={handleChange}
                />
                <span className="field-hint">Ссылка на фотоальбом бота</span>
              </div>
            </div>
          )}

          <div className="modal-actions-footer">
            {onCancel && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onCancel}
                disabled={isSaving}
              >
                ОТМЕНА
              </button>
            )}
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <span className="btn-spinner"></span>
                  <span>СОЗДАНИЕ...</span>
                </>
              ) : (
                <>
                  <IconPlus size={14} />
                  <span>СОЗДАТЬ БОТА</span>
                </>
              )}
            </button>
          </div>
          </fieldset>
        </form>
      </div>
    </div>
  );
};